/**
 * Skio Customer Portal helpers (loaded from theme.liquid on /a/account/* pages).
 *
 * Skio uses its own login, so Shopify Liquid `customer` is usually unavailable here.
 * This script bridges Skio order data with live Shopify variant availability.
 *
 * High-level flow:
 * 1. Intercept Skio's GetOrdersForUser network response → cache orders by order number.
 * 2. Watch the DOM for visible order cards → know which cached orders matter right now.
 * 3. Read ProductVariant.platformId from each line item → query Shopify Storefront API.
 * 4. Attach stock results to cached orders and log for debugging.
 *
 * Stock lookup uses ProductVariant.platformId (Shopify variant GID), not a slug derived
 * from product title. The Theme Ajax API only supports /products/{handle}.js, which
 * requires a handle; the Storefront API nodes query accepts variant GIDs directly.
 *
 * Requires a Storefront API access token in Theme settings → Advanced → Skio custom
 * (exposed via js-variables.liquid as theme.settings.skioStorefrontAccessToken).
 */
(function () {
  const STOREFRONT_API_VERSION = '2026-04';

  // ---------------------------------------------------------------------------
  // Caches & deduplication
  // ---------------------------------------------------------------------------

  /**
   * order number (e.g. "1003") → full Skio order object from GetOrdersForUser.
   * Filled by handleSkioJsonResponse; read by collectOrdersFromDom.
   */
  const orderCacheByNumber = new Map();

  /**
   * Shopify variant GID (or numeric id string) → Storefront API variant node.
   * Avoids re-fetching the same variant across multiple orders / observer runs.
   */
  const variantCache = new Map();

  /**
   * Dedupes in-flight Storefront API requests. Key = sorted comma-joined GID list.
   * If stock check runs twice before the first fetch finishes, both await the same promise.
   */
  const variantFetchPromises = new Map();

  /** Dedupes concurrent checkStockForVisibleOrders() calls (DOM observer + API hook). */
  let stockCheckPromise = null;

  // ---------------------------------------------------------------------------
  // ID helpers (Skio order numbers ↔ Shopify GIDs)
  // ---------------------------------------------------------------------------

  /** "Order #1003" or "#1003" → "1003" — used for DOM headers and cache key lookup. */
  function parseOrderNumber(text) {
    if (!text) return null;
    const match = String(text).match(/#?\s*(\d+)\s*$/);
    return match ? match[1] : null;
  }

  /** "gid://shopify/ProductVariant/48163059335382" → "48163059335382" */
  function parseShopifyNumericId(gid) {
    if (!gid) return null;
    const match = String(gid).match(/(\d+)$/);
    return match ? match[1] : null;
  }

  /**
   * Skio line items expose ProductVariant.platformId as a Shopify GID.
   * Storefront API expects the full GID string in nodes(ids: [...]).
   */
  function toVariantGid(platformId) {
    if (!platformId) return null;

    const value = String(platformId);
    if (value.startsWith('gid://')) return value;

    const numericId = parseShopifyNumericId(value);
    return numericId ? `gid://shopify/ProductVariant/${numericId}` : null;
  }

  /** From js-variables.liquid → theme.settings.skioStorefrontAccessToken */
  function getStorefrontAccessToken() {
    return window.theme?.settings?.skioStorefrontAccessToken || '';
  }

  /**
   * Same-origin GraphQL endpoint (works on custom domain + myshopify.com).
   * e.g. https://your-store.com/api/2024-10/graphql.json
   */
  function getStorefrontGraphqlUrl() {
    return `${window.location.origin}/api/${STOREFRONT_API_VERSION}/graphql.json`;
  }

  // ---------------------------------------------------------------------------
  // Shopify stock lookup (Storefront API)
  // ---------------------------------------------------------------------------

  /**
   * Batch-fetch variant availability for many line items in one GraphQL request.
   *
   * Uses the `nodes` query so we only need ProductVariant.platformId from Skio —
   * no product handle required.
   *
   * Returns a Map keyed by both GID and numeric id for easy lookup in checkLineItemStock.
   */
  async function fetchShopifyVariantsByGids(gids) {
    const token = getStorefrontAccessToken();

    if (!token) {
      console.warn(
        '[skio-custom] Missing Storefront API token. Add it in Theme settings → Advanced → Skio custom.'
      );
      return new Map();
    }

    const uniqueGids = [...new Set(gids.filter(Boolean))];
    if (!uniqueGids.length) return new Map();

    // Same set of GIDs in any order → same cache key → one network request.
    const cacheKey = uniqueGids.slice().sort().join(',');
    if (variantFetchPromises.has(cacheKey)) {
      return variantFetchPromises.get(cacheKey);
    }

    const promise = (async () => {
      const url = getStorefrontGraphqlUrl();
      const query = `
        query ($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on ProductVariant {
              id
              title
              availableForSale
              quantityAvailable
              sku
              product {
                id
                handle
                title
                vendor
                tags
              }
            }
          }
        }
      `;

      console.group('[skio-custom] Storefront API variant fetch');
      console.log('URL:', url);
      console.log('Variant GIDs:', uniqueGids);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({
          query,
          variables: { ids: uniqueGids }
        })
      });

      console.log('Response status:', response.status, response.statusText);

      if (!response.ok) {
        console.warn('Storefront API request failed');
        console.groupEnd();
        return new Map();
      }

      const payload = await response.json();

      if (payload.errors?.length) {
        console.warn('Storefront API GraphQL errors:', payload.errors);
      }

      console.log('Full Storefront API payload:', payload);
      console.groupEnd();

      // Build lookup map: GID and numeric id both point to the same variant node.
      const result = new Map();

      payload.data?.nodes?.forEach((node) => {
        if (!node?.id) return;

        result.set(node.id, node);

        const numericId = parseShopifyNumericId(node.id);
        if (numericId) result.set(numericId, node);
      });

      // Persist for later runs (e.g. DOM observer re-fire before new API response).
      uniqueGids.forEach((gid) => {
        const node = result.get(gid);
        if (node) variantCache.set(gid, node);
      });

      return result;
    })().finally(() => {
      variantFetchPromises.delete(cacheKey);
    });

    variantFetchPromises.set(cacheKey, promise);
    return promise;
  }

  /** Fallback lookup from variantCache if batch map doesn't have this variant. */
  function getCachedShopifyVariant(platformId) {
    const gid = toVariantGid(platformId);
    if (!gid) return null;

    return variantCache.get(gid) || variantCache.get(parseShopifyNumericId(gid)) || null;
  }

  /**
   * Normalize Skio line item + Shopify variant node into a single stock result object.
   * Keeps checkLineItemStock thin — all field naming lives here.
   */
  function buildStockResult(lineItem, shopifyVariant) {
    const skioVariant = lineItem?.ProductVariant;
    const skioProduct = skioVariant?.Product;
    const variantGid = toVariantGid(skioVariant?.platformId);
    const variantId = parseShopifyNumericId(variantGid);

    if (!skioVariant?.platformId) {
      return {
        error: 'missing ProductVariant.platformId',
        available: null
      };
    }

    if (!shopifyVariant) {
      return {
        skioProductTitle: skioProduct?.title ?? null,
        variantGid,
        variantId,
        variantFound: false,
        available: null,
        vendor: shopifyVariant.product?.vendor ?? null,
        tags: shopifyVariant.product?.tags ?? null
      };
    }

    return {
      skioProductTitle: skioProduct?.title ?? null,
      variantGid,
      variantId,
      variantFound: true,
      available: shopifyVariant.availableForSale ?? null,
      quantityAvailable: shopifyVariant.quantityAvailable ?? null,
      shopifyVariantTitle: shopifyVariant.title ?? null,
      shopifyProductHandle: shopifyVariant.product?.handle ?? null,
      shopifyProductTitle: shopifyVariant.product?.title ?? null,
      sku: shopifyVariant.sku ?? null,
      vendor: shopifyVariant.product?.vendor ?? null,
      tags: shopifyVariant.product?.tags ?? null
    };
  }

  /**
   * Match one Skio OrderLineItem to Shopify stock using platformId only.
   * variantLookup is the Map returned by fetchShopifyVariantsByGids (batch result).
   */
  async function checkLineItemStock(lineItem, variantLookup) {
    const skioVariant = lineItem?.ProductVariant;
    const variantGid = toVariantGid(skioVariant?.platformId);

    if (!variantGid) {
      return buildStockResult(lineItem, null);
    }

    const shopifyVariant =
      variantLookup?.get(variantGid) ||
      variantLookup?.get(parseShopifyNumericId(variantGid)) ||
      getCachedShopifyVariant(skioVariant.platformId);

    return buildStockResult(lineItem, shopifyVariant);
  }

  /**
   * For each order card visible on the page:
   * 1. Collect all variant GIDs from cached Skio OrderLineItems.
   * 2. One batched Storefront API call for the whole page.
   * 3. Write __shopifyStock onto each cached order object.
   */
  async function checkStockForVisibleOrders() {
    const orders = collectOrdersFromDom();
    const results = [];
    const gids = [];

    orders.forEach((order) => {
      order.apiData?.OrderLineItems?.forEach((lineItem) => {
        const gid = toVariantGid(lineItem?.ProductVariant?.platformId);
        if (gid) gids.push(gid);
      });
    });

    const variantLookup = await fetchShopifyVariantsByGids(gids);

    for (const order of orders) {
      const lineItems = order.apiData?.OrderLineItems;

      if (!lineItems?.length) continue;

      const lineStock = await Promise.all(
        lineItems.map((item) => checkLineItemStock(item, variantLookup))
      );

      order.apiData.__shopifyStock = lineStock;
      results.push({ orderNumber: order.orderNumber, lineStock });
    }

    return results;
  }

  /** Coalesce overlapping stock checks into a single in-flight promise. */
  function scheduleStockCheck() {
    if (stockCheckPromise) return stockCheckPromise;

    stockCheckPromise = checkStockForVisibleOrders()
      .then(() => applyStockUiToOrderCards())
      .finally(() => {
        stockCheckPromise = null;
      });

    return stockCheckPromise;
  }

  // ---------------------------------------------------------------------------
  // DOM: out-of-stock UI on order cards
  // ---------------------------------------------------------------------------

  function isVariantOutOfStock(stock) {
    return stock?.variantFound === true && (stock.available === false || stock.quantityAvailable <= 0);
  }

  function isVariantDiscontinued(stock) {
    return stock?.vendor === "Legacy Import" || Array.from(stock?.tags).includes("HISTORICAL_IMPORT");
  }

  function getProductUrl(handle) {
    if (!handle) return null;

    const root = (window.theme?.routes?.root_url || '/').replace(/\/?$/, '/');
    return `${root}products/${handle}`;
  }

  function ensureStockUiStyles() {
    if (document.getElementById('skio-custom-stock-ui-styles')) return;

    const style = document.createElement('style');
    style.id = 'skio-custom-stock-ui-styles';
    style.textContent = `
      [data-skio-name="order-card-item"][data-skio-custom-out-of-stock="true"] {
        opacity: 0.72;
      }

      a[data-skio-custom="product-link"] {
        color: inherit;
        text-decoration: none;
      }

      a[data-skio-custom="product-link"]:hover {
        text-decoration: underline;
      }
    `;
    document.head.appendChild(style);
  }

  function findStockForDomItem(itemEl, stockList) {
    const title = itemEl
      .querySelector('[data-skio-name="order-card-item-title"]')
      ?.textContent?.trim()
      .toLowerCase();

    if (!title) return null;

    return (
      stockList.find((stock) => stock.skioProductTitle?.trim().toLowerCase() === title) || null
    );
  }

  function resetOrderCardStockUi(card) {
    card.querySelectorAll('[data-skio-custom="out-of-stock-badge"]').forEach((el) => el.remove());

    card.querySelectorAll('[data-skio-name="order-card-item"]').forEach((item) => {
      item.removeAttribute('data-skio-custom-out-of-stock');
    });

    card.querySelectorAll('a[data-skio-custom="product-link"]').forEach((link) => {
      const title = document.createElement('p');
      title.setAttribute('data-skio-name', 'order-card-item-title');
      title.className = link.className.replace(/\s*hover:underline/g, '').trim();
      title.textContent = link.textContent;
      link.replaceWith(title);
    });
  }

  function addProductBadge(itemEl, type) {
    if (itemEl.querySelector('[data-skio-custom="out-of-stock-badge"]')) return;

    const badge = document.createElement('span');
    badge.setAttribute('data-skio-custom', 'out-of-stock-badge');
    badge.className =
      'inline-flex items-center rounded-border-radius bg-[#fee2e2] px-2 py-0.5 text-xs font-semibold leading-[18px] text-[#dc2626]';
    if (type === 'out-of-stock') {
      badge.textContent = 'Out of stock';
    } else if (type === 'discontinued') {
      badge.textContent = 'Discontinued';
    }

    const titleRow = itemEl.querySelector('[data-skio-name="order-card-item-title"]')?.parentElement;
    (titleRow || itemEl).appendChild(badge);
    itemEl.setAttribute('data-skio-custom-out-of-stock', 'true');
  }

  function applyProductTitleLink(itemEl, stock) {
    const productUrl = getProductUrl(stock?.shopifyProductHandle);
    if (!productUrl) return;

    const titleEl = itemEl.querySelector('[data-skio-name="order-card-item-title"]');
    if (!titleEl || titleEl.tagName === 'A') return;

    const link = document.createElement('a');
    link.href = productUrl;
    link.setAttribute('data-skio-name', 'order-card-item-title');
    link.setAttribute('data-skio-custom', 'product-link');
    link.className = `${titleEl.className} hover:underline`.trim();
    link.textContent = titleEl.textContent;
    titleEl.replaceWith(link);
  }

  function removeReorderButton(card) {
    card.querySelector('[data-skio-name="order-card-reorder-button"]')?.remove();
  }

  /**
   * After __shopifyStock is populated, sync Skio order card DOM:
   * - link each line item title to its Shopify product page
   * - badge on each unavailable line item
   * - remove Order again only when every line item is unavailable
   */
  function applyStockUiToOrderCards() {
    ensureStockUiStyles();

    document.querySelectorAll('[data-skio-name="order-card"]').forEach((card) => {
      resetOrderCardStockUi(card);

      const orderNumber = parseOrderNumber(
        card.querySelector('[data-skio-name="order-card-header-number"]')?.textContent
      );

      if (!orderNumber) return;

      const stockList = orderCacheByNumber.get(orderNumber)?.__shopifyStock;
      if (!stockList?.length) return;

      const itemEls = [...card.querySelectorAll('[data-skio-name="order-card-item"]')];
      let allOutOfStock = itemEls.length > 0;

      itemEls.forEach((itemEl, index) => {
        const stock = stockList[index] ?? findStockForDomItem(itemEl, stockList);

        applyProductTitleLink(itemEl, stock);

        if (isVariantDiscontinued(stock)) {
          addProductBadge(itemEl, 'discontinued');
        } else if (isVariantOutOfStock(stock)) {
          addProductBadge(itemEl, 'out-of-stock');
        } else {
          allOutOfStock = false;
        }
      });

      if (allOutOfStock) {
        removeReorderButton(card);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // DOM: which orders are on screen?
  // ---------------------------------------------------------------------------

  /**
   * Reads order numbers from Skio's rendered order cards and joins them to orderCacheByNumber.
   * Does not fetch data — only answers "which orders are visible?" and looks up cached API data.
   */
  function collectOrdersFromDom() {
    const headers = document.querySelectorAll('p[data-skio-name="order-card-header-number"]');
    const orders = [];
    const seen = new Set();

    headers.forEach((header) => {
      const orderNumber = parseOrderNumber(header.textContent);
      if (!orderNumber || seen.has(orderNumber)) return;
      seen.add(orderNumber);

      const cached = orderCacheByNumber.get(orderNumber);

      orders.push({
        orderNumber,
        headerText: header.textContent.replace(/\s+/g, ' ').trim(),
        apiData: cached || null
      });
    });

    return orders;
  }

  // ---------------------------------------------------------------------------
  // Skio network: capture GetOrdersForUser responses
  // ---------------------------------------------------------------------------

  /**
   * Gate: only the orders-list GraphQL response has this shape.
   * Ignores subscriptions, auth, and other Skio JSON on the same page.
   */
  function isGetOrdersForUserResponse(json) {
    const orders = json?.data?.Orders;
    if (!Array.isArray(orders)) return false;

    return orders.every((order) => order?._GetOrdersForUser === 'Order');
  }

  /** All string keys under which one order should be findable in orderCacheByNumber. */
  function getOrderCacheKeys(order) {
    return [
      order.platformNumber,
      order.name,
      order.orderNumber,
      order.number
    ]
      .filter(Boolean)
      .map((value) => parseOrderNumber(String(value)) || String(value).replace(/\D/g, ''))
      .filter(Boolean);
  }

  /**
   * Called when interceptors capture a GetOrdersForUser response.
   * Indexes orders, then triggers stock check + logging.
   */
  function handleSkioJsonResponse(url, json) {
    const orders = json.data.Orders;

    console.log('[skio-custom] GetOrdersForUser response', {
      url,
      orderCount: orders.length,
      orderNumbers: orders.map((order) => order.platformNumber || order.name)
    });

    orders.forEach((order) => {
      if (order?._GetOrdersForUser !== 'Order') return;

      const cacheEntry = { ...order };
      // __shopifyStock is added by us later; strip it so a fresh API payload doesn't keep stale stock.
      delete cacheEntry.__shopifyStock;

      getOrderCacheKeys(order).forEach((key) => {
        orderCacheByNumber.set(key, cacheEntry);
      });
    });

    scheduleStockCheck().then(() => logSkioOrders('GetOrdersForUser'));
  }

  /**
   * Wraps window.fetch to read Skio JSON responses without affecting Skio's own handling.
   *
   * Only URLs matching /skio/ are considered. Of those, only GetOrdersForUser-shaped
   * JSON is passed to handleSkioJsonResponse — everything else is ignored.
   */
  function installNetworkInterceptors() {
    if (window.__skioCustomNetworkHooked) return;
    window.__skioCustomNetworkHooked = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = async function (...args) {
      const response = await originalFetch(...args);

      try {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
        if (!/skio/i.test(url)) return response;

        // Clone the response to avoid mutating the original response.
        const clone = response.clone();
        const contentType = clone.headers.get('content-type') || '';

        // Only process JSON responses.
        if (contentType.includes('json')) {
          const json = await clone.json();
          if (!isGetOrdersForUserResponse(json)) return response;
          handleSkioJsonResponse(url, json);
        }
      } catch {
        // Ignore parse errors from non-JSON responses.
      }

      return response;
    };
  }

    // Uncomment to access the Skio Custom API (used for debugging)
  // window.skioCustom = {
  //   logOrders: () => scheduleStockCheck().then(() => logSkioOrders('manual')),
  //   getOrderCache: () => Object.fromEntries(orderCacheByNumber),
  //   isGetOrdersForUserResponse,
  //   titleToHandle,
  //   checkLineItemStock,
  //   checkStockForVisibleOrders,
  //   getProductCache: () => Object.fromEntries(productCacheByHandle)
  // };

  // ---------------------------------------------------------------------------
  // Logging & DOM observer
  // ---------------------------------------------------------------------------

  function logSkioOrders(source) {
    const orders = collectOrdersFromDom();

    console.group(`[skio-custom] Orders from page (${source})`);
    if (orders.length) {
      console.log('Order numbers found:', orders.map((o) => o.orderNumber));
    } else {
      console.log('No order cards found yet. Waiting for Skio portal DOM…');
    }

    orders.forEach((order) => {
      console.group(`Order #${order.orderNumber}`);
      console.log('Header:', order.headerText);

      if (order.apiData) {
        console.log('API payload (matched):', order.apiData);

        if (order.apiData.__shopifyStock) {
          console.log('Shopify stock (Storefront API by variant GID):', order.apiData.__shopifyStock);
        }
      } else {
        console.log('API payload: not captured yet (open Orders tab or wait for portal to load)');
      }

      console.groupEnd();
    });

    console.groupEnd();
  }

  /**
   * Skio mounts order cards asynchronously. MutationObserver re-runs stock + logs
   * when cards appear or the list updates (childList + subtree on #skio-root).
   */
  function watchSkioOrdersDom() {
    const root = document.querySelector('#skio-root') || document.body;
    const run = () => {
      if (!document.querySelector('p[data-skio-name="order-card-header-number"]')) return;
      scheduleStockCheck().then(() => logSkioOrders('dom'));
    };

    const observer = new MutationObserver(run);
    observer.observe(root, { childList: true, subtree: true });

    run();
  }

  document.addEventListener('DOMContentLoaded', () => {
    installNetworkInterceptors();
    watchSkioOrdersDom();
  });
})();
